import React, {useEffect, useState} from 'react';
import {Select, Spin, SelectProps, Tag} from 'antd';
import {OptionItem} from '../../types';
import {useOptions} from './hooks/useOptions';
import {useTranslation} from '../../hooks/useTranslation';

interface SelectOptionProps {
  value?: string | string[];
  placeholder?: string;
  onChange?: (value: string | string[], option?: OptionItem | OptionItem[]) => void;
  optionType?: string; // Type to filter options (e.g., 'ISSUE_ORIGIN', 'ISSUE_EFFECT', etc.)
  disabled?: boolean;
  allowClear?: boolean;
  optionKey?: string;
  isFilterable?: boolean;
  size?: 'small' | 'middle' | 'large';
  style?: React.CSSProperties;
  className?: string;
  mode?: 'multiple' | 'tags';
  id?: string;
  initialOptions?: OptionItem[];
}

type TagRender = SelectProps['tagRender'];
const SelectOption: React.FC<SelectOptionProps> = ({
                                                     value,
                                                     placeholder,
                                                     onChange,
                                                     optionKey,
                                                     optionType = 'ISSUE',
                                                     isFilterable = false,
                                                     disabled = false,
                                                     allowClear = true,
                                                     size = 'middle',
                                                     style,
                                                     className,
                                                     mode,
                                                     id,
                                                     initialOptions = []
                                                   }) => {
  const {t} = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [mergedOptions, setMergedOptions] = useState<OptionItem[]>(initialOptions);

  // Use the existing useOptions hook to fetch data - only when the dropdown is opened
  const {data: options = [], isLoading, error} = useOptions(
    {
      propertyFind: 'f.identified',
      valueFind: optionType,
      propertyOrder: 'f.description',
      isFilterable: isFilterable,
      optionKey: optionKey,
      limit: 100
    },
    {
      enabled: isDropdownOpen // Only fetch data when the dropdown is opened
    }
  );

  // Merge initial options with loaded options to avoid duplicates
  useEffect(() => {
    if (options.length > 0) {
      // Create a map of existing option IDs from loaded options
      const loadedOptionIds = new Set(options.map(opt => opt.id || opt.value));

      // Filter out initial options that are already in loaded options
      const uniqueInitialOptions = initialOptions.filter(
        initOpt => !loadedOptionIds.has(initOpt.id || initOpt.value)
      );

      // Merge: initial options first, then loaded options
      setMergedOptions([...uniqueInitialOptions, ...options]);
    } else if (initialOptions.length > 0) {
      // If no options loaded yet, just show initial options
      setMergedOptions(initialOptions);
    }
  }, [options, initialOptions]);

  const handleDropdownVisibleChange = (open: boolean) => {
    setIsDropdownOpen(open);
  };

  const handleChange = (selectedValue: string | string[] | undefined) => {
    if (selectedValue === undefined || selectedValue === null || selectedValue === '') {
      // Handle clear action
      if (onChange) {
        onChange('', {} as OptionItem);
      }
      return;
    }

    // Handle multiple mode
    if (mode === 'multiple' && Array.isArray(selectedValue)) {
      // For multiple mode, find all selected options and notify parent
      const selectedOptions = selectedValue.map(val =>
        mergedOptions.find(option => option.id === val || option.value === val)
      ).filter(Boolean) as OptionItem[];

      if (onChange) {
        onChange(selectedValue, selectedOptions);
      }
      return;
    }

    // Handle single selection mode
    const selectedOption = mergedOptions.find(option =>
      option.id === selectedValue || option.value === selectedValue
    );

    if (onChange && selectedOption) {
      onChange(selectedValue as string, selectedOption);
    }
  };

  const handleSearch = (searchText: string) => {
    setSearchValue(searchText);
  };

  const filterOption = (input: string, option?: any) => {
    if (!input) return true;
    const searchText = input.toLowerCase();
    const optionData = mergedOptions.find(opt =>
      opt.id === option?.value || opt.value === option?.value
    );

    if (!optionData) return false;

    return (
      (optionData.description?.toLowerCase().includes(searchText) || false) ||
      (optionData.abbreviation?.toLowerCase().includes(searchText) || false) ||
      (optionData.name?.toLowerCase().includes(searchText) || false) ||
      (optionData.value?.toLowerCase().includes(searchText) || false)
    );
  };

  if (error) {
    console.error('Error loading options:', error);
  }

  const tagRender: TagRender = (props) => {
    const {label, value, closable, onClose} = props;
    const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };
    return (
      <Tag
        onMouseDown={onPreventMouseDown}
        onClose={onClose}
        closable={closable}
        color={"default"}
        style={{fontSize: 14, marginInlineEnd: 4, height: 24}}
      >{label}
      </Tag>
    );
  };

  return (
    <Select
      id={id}
      value={value}
      placeholder={placeholder || t('messages.select')}
      onChange={handleChange}
      onSearch={handleSearch}
      onOpenChange={handleDropdownVisibleChange}
      disabled={disabled}
      allowClear={allowClear}
      size={size}
      style={{width: '100%'}}
      className={className}
      mode={mode}
      showSearch
      optionFilterProp="children"
      filterOption={filterOption}
      // tagRender={tagRender}
      loading={isLoading}
      notFoundContent={isLoading ? <Spin size="small"/> : t('messages.notFound')}
    >
      {mergedOptions.map((option) => (
        <Select.Option
          key={option.id || option.value}
          value={option.id || option.value}
          title={option.description}
        >
          <div>
            {option.color && (
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: option.color,
                  marginRight: 8,
                  border: '1px solid #d9d9d9'
                }}
              />
            )}
          </div>
          {option.description || option.name || option.value}
        </Select.Option>
      ))}
    </Select>
  );
};

export default SelectOption;
